import { ListRenderItem } from '@shopify/flash-list';
import { Card } from 'components/Card';
import { PageFlatList } from 'components/Page';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { ExplorerSummaryRow } from 'screens/AssessmentScreen/Explorer/ExplorerSummaryRow';
import {
  selectAseCanChangeStatus,
  selectAseCannotEditReason,
  selectAseTplFieldSectionsForExplorer,
  selectAseTplHasFlags,
  selectAseTplHasSignatories,
  selectAseTplSectionIds,
} from 'store/assessments/selectors';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { StatusChange } from '../StatusChange';
import { ExplorerAssetRow } from './ExplorerAssetRow';
import { ExplorerHeader } from './ExplorerHeader';
import { ExplorerQuestionsHeader } from './ExplorerQuestionsHeader';
import { ExplorerRepeatableRow } from './ExplorerRepeatableRow';
import { ExplorerSection } from './ExplorerSection';
import { ExplorerNavigation } from './types';
import { ExplorerFieldGroupRow } from './ExplorerFieldGroupRow';
import { MessageEdit } from '../MessageEdit';
import { ExplorerFlags } from './ExplorerFlags';
import { ExplorerSignatories } from './ExplorerSignatories';

interface Props {
  navigation: ExplorerNavigation;
}

type RenderItem =
  | {
      type: 'overview';
    }
  | {
      type: 'fields';
    }
  | {
      type: 'section';
      data: {
        id: string;
      };
    };

export const Explorer: React.FC<Props> = ({ navigation }) => {
  const fieldsList = useSelector(selectAseTplFieldSectionsForExplorer);
  const sectionIds = useSelector(selectAseTplSectionIds);
  const canChangeStatus = useSelector(selectAseCanChangeStatus);
  const cannotEditReason = useSelector(selectAseCannotEditReason);
  const hasSignatories = useSelector(selectAseTplHasSignatories);
  const hasFlags = useSelector(selectAseTplHasFlags);

  const renderItem: ListRenderItem<RenderItem> = React.useCallback(
    ({ item }) => {
      if (item.type === 'overview') {
        return (
          <>
            <ViewWithSeparators
              separatorMargin={theme.paddingHorizontalHalf / 2}
              separatorColor="transparent"
            >
              {cannotEditReason && <MessageEdit />}
              <ExplorerSummaryRow navigation={navigation} />
              {canChangeStatus && <StatusChange />}
              {hasSignatories && <ExplorerSignatories />}
              {hasFlags && <ExplorerFlags />}
            </ViewWithSeparators>
            <Card />
            <SeparatorHorizontal withMarginBottom />
          </>
        );
      }
      if (item.type === 'fields') {
        return (
          <>
            <Card
              label="Address and Photo"
              style={{ marginBottom: theme.paddingVertical / 2 }}
            >
              <ExplorerAssetRow navigation={navigation} />
            </Card>
            {fieldsList.map((section) => (
              <Card
                key={section.id}
                label={section.name}
                style={{ marginBottom: theme.paddingVertical / 2 }}
              >
                {section.groups.map((group) =>
                  group.repeatable ? (
                    <ExplorerRepeatableRow
                      key={group.id}
                      groupId={group.id}
                      parent={null}
                    />
                  ) : (
                    <ExplorerFieldGroupRow key={group.id} groupId={group.id} />
                  ),
                )}
              </Card>
            ))}
            <SeparatorHorizontal
              style={{
                marginBottom: theme.paddingVertical / 2,
                marginTop: theme.paddingVertical / 2,
              }}
            />
          </>
        );
      }
      if (item.type === 'section') {
        return <ExplorerSection key={item.data.id} id={item.data.id} />;
      }
      return null;
    },
    [
      canChangeStatus,
      cannotEditReason,
      fieldsList,
      hasFlags,
      hasSignatories,
      navigation,
    ],
  );

  return React.useMemo(
    () => (
      <PageFlatList
        isInModal={false}
        header={
          <View>
            <ExplorerHeader />
          </View>
        }
        underHeaderAbsolute={<ExplorerQuestionsHeader />}
        contentPaddingBottom={400}
        listConfig={{
          getItemType: (item) => item.type,
          data: [
            { type: 'overview' },
            { type: 'fields' },
            ...sectionIds.map<RenderItem>((sectionId) => ({
              type: 'section',
              data: { id: sectionId },
            })),
          ],
          keyExtractor: (item) =>
            `${item.type}-${item.type === 'section' ? `${item.data.id}` : ''}`,
          renderItem,
          ListHeaderComponent: (
            <View
              style={{
                height: theme.paddingPageVertical + 50 + 12,
              }}
            />
          ),
        }}
      />
    ),
    [renderItem, sectionIds],
  );
};
